import { useRef } from 'react';

import { useSafeLayoutEffect } from '../../../../core/hooks/useSafeLayoutEffect.js';
import { usePageLayoutPortalContext } from './PageLayoutPortalContext.js';
import type { SlotOwner, SlotRegistryState } from './slot-registry.js';

/**
 * Registers `entry` for `slotName` in the slot registry on mount and
 * unregisters on unmount. Re-registers when `entry` identity changes.
 *
 * Uses a layout effect so the registration is observable before the next
 * paint: the layout shells (`MainShell`, `HeaderShell`) need to see the
 * registered entry in the same commit as the slot component mounts to avoid
 * a frame of unstyled content.
 *
 * Callers must keep `entry` referentially stable, which is what `useSlotEntry`
 * is for, so the effect only re-runs when something meaningful changes.
 *
 * Each caller registers under an owner identity that lives as long as the
 * component instance, so a cleanup that runs after a replacement has already
 * claimed the slot cannot clear the live registration.
 *
 * @internal
 */
export function useRegisteredSlot<K extends keyof SlotRegistryState>(
  slotName: K,
  entry: NonNullable<SlotRegistryState[K]>,
): void {
  const { registry } = usePageLayoutPortalContext();
  const ownerRef = useRef<SlotOwner>({});
  useSafeLayoutEffect(() => {
    const owner = ownerRef.current;
    registry.register(slotName, entry, owner);
    return () => registry.unregister(slotName, owner);
  }, [registry, slotName, entry]);
}
